import { Map } from 'immutable'
import {
  setIn,
  updateIn,
  removeIn,
  push,
  filterNot,
  mergeDeep,
  merge,
  setAllIn,
} from 'utils/immutable'
import { compose, equals, identity } from 'ramda'
import {
  DEFAULT_POWER_NET_LEVEL,
  DEFAULT_KEEP_AWAKE,
} from 'constants/featureOutputs'
import {
  CHECPLUS_MODULE_TYPES,
  RESET_TYPES,
  CAN,
  DEFAULT_OUTPUT_BITS,
  PIN,
  TIMER,
  NATIVE_INPUT,
  SIMPLE,
  AH,
} from 'utils/constants'
import { OPERATORS, EVAL_VALUES } from 'components/utils'
import strings from 'utils/strings'
import { makeTempId } from 'utils/identity'
import { canOutputId } from 'components/feature/utils/outputs'
import * as actions from 'actions/feature/outputs'

const pathToConditions = ['currentFeature', 'conditions']

export default {
  [actions.addOutputNode]: addOutputNode,
  [actions.deleteOutputNode]: deleteOutputNode,
  [actions.updateOutputQuery]: updateOutputQuery,
  [actions.resetOutputNode]: resetOutputNode,
  [actions.setCanId]: setCanId,
  [actions.setScalingInputField]: setScalingInputField,
  [actions.createScalingQuery]: createScalingQuery,
  [actions.removeScalingQuery]: removeScalingQuery,
  [actions.resetOutputFloatingCircuits]: resetFloatingCircuits,
  [actions.updateOutputNode]: updateOutputNode,
}

const initialOutputXMC = {
  moduleType: CHECPLUS_MODULE_TYPES.XMC,
  mappedOutputId: null,
  id: 'tmp_output',
  powerNetLevel: DEFAULT_POWER_NET_LEVEL,
  isAwake: DEFAULT_KEEP_AWAKE,
}

const initialOutputASAM = {
  moduleType: CHECPLUS_MODULE_TYPES.ASAM,
  mappedOutputId: null,
  id: 'tmp_output',
}

const getModuleInitialByType = (
  moduleType,
  tmpId,
  fnId,
  newIndex,
  hasTimerQuery
) => {
  const types = {
    XMC: initialOutputXMC,
    ASAM: initialOutputASAM,
  }

  const evaluation = {
    operator: OPERATORS.EQUAL,
    value: hasTimerQuery ? 0 : EVAL_VALUES.TRUE,
  }
  return {
    ...types[moduleType],
    ...(newIndex && { description: `${strings.output} ${newIndex}` }),
    ...(tmpId && { id: tmpId }),
    ...(fnId && { fnId }),
    ...evaluation,
    isAwake: DEFAULT_KEEP_AWAKE,
  }
}

function updateOutputNode(state, { id, nodeSubset }) {
  return state.mergeIn(['currentFeature', 'outputs', id], nodeSubset)
}

function setScalingInputField(state, { paramName, value, id }) {
  return state.setIn(
    ['currentFeature', 'outputs', id, 'scalingQuery', paramName],
    value
  )
}

function addOutputNode(state, { outputType, fnId, tmpId, newIndex }) {
  const outputQueryUri = state.getIn([
    'currentFeature',
    'outputMaps',
    fnId,
    'outputQuery',
  ])
  const outputQueryType = state.getIn([
    'currentFeature',
    'queries',
    outputQueryUri,
    'queryType',
  ])
  const hasTimerQuery = outputQueryType === TIMER
  const newOutput = getModuleInitialByType(
    outputType,
    tmpId,
    fnId,
    newIndex,
    hasTimerQuery
  )

  const addOutput = setIn(['currentFeature', 'outputs', tmpId], newOutput)

  const appendToFnOutputs = updateIn(
    ['currentFeature', 'outputMaps', fnId, 'outputs'],
    push(tmpId)
  )

  return compose(addOutput, appendToFnOutputs)(state)
}

function deleteOutputNode(state, { id, fnId }) {
  const removeOutput = removeIn(['currentFeature', 'outputs', id])

  const removeOutputFromMap = updateIn(
    ['currentFeature', 'outputMaps', fnId, 'outputs'],
    filterNot(equals(id))
  )

  return compose(removeOutput, removeOutputFromMap)(state)
}

function resetOutputNode(state, { id }) {
  const outputPath = ['currentFeature', 'outputs', id]
  const outputType = state.getIn([...outputPath, 'type'])
  const originalFeatureOutputCurrent = state.getIn([
    'originalFeature',
    'outputs',
    id,
    'current',
  ])
  const originalFeatureMappedOutputId = state.getIn([
    'originalFeature',
    'outputs',
    id,
    'mappedOutputId',
  ])
  const originalFeaturePolarity = state.getIn([
    'originalFeature',
    'outputs',
    id,
    'polarity',
  ])

  const moduleName = getFeatureMetaField('moduleName', state)

  const isXMC = moduleName.includes(CHECPLUS_MODULE_TYPES.XMC)
  const isCAN = outputType === CAN
  const isPIN = outputType === PIN

  const updateData = Map({
    startBit: null,
    polarity: null,
    current: originalFeatureOutputCurrent || 5,
    resetType: RESET_TYPES.IGNITION,
    virtualFuseSetting: 20,
    resetAttempts: 0,
    dutyCycle: 10,
    frequency: 20,
    ...(isXMC &&
      isPIN && {
        powerNetLevel: DEFAULT_POWER_NET_LEVEL,
        isAwake: DEFAULT_KEEP_AWAKE,
        mappedOutputId: originalFeatureMappedOutputId,
        polarity: originalFeaturePolarity || AH,
      }),
    ...(!isCAN && { mappedOutputId: null }),
    ...(isCAN && {
      numberOfBits: DEFAULT_OUTPUT_BITS[moduleName],
      mappedOutputId: canOutputId(moduleName, DEFAULT_OUTPUT_BITS[moduleName]),
    }),
  })

  return state.updateIn(outputPath, merge(updateData))
}

function updateOutputQuery(state, { queryUri, fnId }) {
  return compose(
    setIn(['currentFeature', 'outputMaps', fnId, 'outputQuery'], queryUri)
  )(state)
}

// TODO: move to inputs
function setCanId(state, { canId, joinQueryUri }) {
  const features = state.getIn(['currentFeature', 'feature'])
  const featureId = features.keySeq().first()
  const canIds = state.getIn(['currentFeature', 'feature', featureId, 'canIds'])
  const featureAlreadyHasThisCanId = canIds && canIds.indexOf(canId) > -1
  // This may not be necessary if/when backend always sends
  // an array to represent canIds on the feature:
  const maybeInitializeCanIds = canIds
    ? identity
    : mergeDeep({
        currentFeature: { feature: { [featureId]: { canIds: [] } } },
      })
  const maybeUpdateList = featureAlreadyHasThisCanId
    ? identity
    : updateIn(['currentFeature', 'feature', featureId, 'canIds'], (ids) =>
        ids.push(canId)
      )
  return compose(
    maybeUpdateList,
    maybeInitializeCanIds,
    setIn([...pathToConditions, joinQueryUri, 'canId'], canId)
  )(state)
}

function createScalingQuery(state, { id }) {
  const tempId = makeTempId('scalingQuery') // TODO: use library tool

  const setUriInOutput = setIn(
    ['currentFeature', 'outputs', id, 'scalingQuery'],
    tempId
  )
  const addQuery = setIn(
    ['currentFeature', 'conditions', tempId],
    Map({
      uri: tempId,
      valueMin: 0,
      valueMax: 0,
      signal: null,
      subType: NATIVE_INPUT,
      queryType: SIMPLE,
    })
  )

  const transform = compose(setUriInOutput, addQuery)

  return transform(state)
}

function removeScalingQuery(state, { id }) {
  const scalingQueryPath = ['currentFeature', 'outputs', id, 'scalingQuery']
  const scalingQueryUri = state.getIn(scalingQueryPath)
  const removeScalingQueryUri = removeIn(scalingQueryPath)
  const removeQuery = removeIn([
    'currentFeature',
    'conditions',
    scalingQueryUri,
  ])

  const transform = compose(removeScalingQueryUri, removeQuery)

  return transform(state)
}

const pathToFloatingCircuit = (id) => [
  'currentFeature',
  'outputs',
  id,
  'floatingCircuit',
]

function resetFloatingCircuits(state, { ids }) {
  return setAllIn(ids, pathToFloatingCircuit, null, state)
}

// helpers
const getFeatureMetaField = (prop, state) =>
  state.getIn(['currentFeature', 'feature']).valueSeq().first().get(prop)
